import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Button from '@fec/frontend/elements/buttons/button/button';
import useScrollDirection from '@fec/frontend/foundation/hooks/ScrollDirection';

import './LivetickerUpdater.scss';

const LivetickerUpdater = ({ nrOfNewMessages = 0, onClick = () => {} }) => {
  const { t } = useTranslation();
  const mastheadHeight = useRef(0);
  const [rotations, setRotations] = useState(0);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [ignoreScrollEvents, setIgnoreScrollEvents] = useState(false);

  useScrollDirection({
    onScrollUp: () => !ignoreScrollEvents && setScrolledAway(false),
    onScrollDown: () => !ignoreScrollEvents && setScrolledAway(true),
    enabled: nrOfNewMessages > 0 && !ignoreScrollEvents,
  });

  useEffect(() => {
    const masthead = document.querySelector('.js-masthead');
    if (masthead) {
      mastheadHeight.current = masthead.getBoundingClientRect().height;
    }
    // Reset scrolledAway when nrOfNewMessages changes
    if (nrOfNewMessages > 0) {
      setScrolledAway(false);
      setIgnoreScrollEvents(true); // ignore scroll events for a little bit after showing new messages
      const timeout = setTimeout(() => setIgnoreScrollEvents(false), 2000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }
  }, [nrOfNewMessages]);

  // use useLayoutEffect to update rotations at the same time as the number in brackets
  useLayoutEffect(() => {
    if (nrOfNewMessages > 0) {
      setRotations((prevState) => prevState + 1);
    }
  }, [nrOfNewMessages]);

  return (
    <div
      className={classNames('liveticker-updater', {
        'liveticker-updater--visible': nrOfNewMessages > 0,
        'liveticker-updater--scrolled-away': scrolledAway,
      })}
      style={{
        '--masthead-height': mastheadHeight?.current ?? 0,
        '--svg-icon-rotation': -rotations * 360,
        '--svg-icon-rotation-delay': '0.3s',
      }}>
      {/* Button to show the newest messages */}
      {nrOfNewMessages > 0 && (
        <Button
          styleModifier={classNames(
            'button--alert',
            'button--pill',
            'button--pill-narrow-padding',
            'button--mixed-case',
            'button--no-spacing',
            'button--full-width',
            'button--elevation-4',
            'button--with-tabular-num',
          )}
          svgIcon={classNames(
            'svg-icon--retry',
            'svg-icon--adjust-to-container',
            'svg-icon--rotatable',
          )}
          buttonText={t('showNewestTickerEntries', { count: nrOfNewMessages })}
          onClick={onClick}
          additionalAttributes={{ 'aria-live': 'polite' }}
        />
      )}
    </div>
  );
};

export default LivetickerUpdater;
